import { Spinner, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import type React from 'react'
import { useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'
import DefaultModal from '../../../../../../components/DefaultModal'
import FormInput from '../../../../../../components/FormInput'
import FormSelect from '../../../../../../components/FormSelect'
import Toast from '../../../../../../components/Toast'
import { useInviteStaffMemberMutation } from '../../../../../../redux/services/coreApi'
import type { Role } from '../../../../../../types/coreApi-types'

interface AddTeamMemberModalProps {
  isOpen: boolean
  onClose: () => void
}

// The staff roles an admin can provision through this flow. Kept in lockstep with the API's
// IsInvitableStaffRole / Team_Invite guard — Admin is deliberately not offered.
const ROLE_OPTIONS: { label: string; role: Role }[] = [
  { label: 'Compliance Officer', role: 'ComplianceOfficer' },
  { label: 'Finance Officer', role: 'FinanceOfficer' },
  { label: 'Operations Officer', role: 'OperationsOfficer' },
  { label: 'Bookkeeper', role: 'Bookkeeper' },
]

const ROLE_LABELS = ROLE_OPTIONS.map(o => o.label)

type FormData = {
  firstName: string
  lastName: string
  email: string
  phone: string
  roleLabel: string
}

const AddTeamMemberModal: React.FC<AddTeamMemberModalProps> = ({
  isOpen,
  onClose,
}) => {
  const schema: yup.SchemaOf<FormData> = yup.object({
    firstName: yup.string().required('This field is required.'),
    lastName: yup.string().required('This field is required.'),
    email: yup
      .string()
      .email('Please enter a valid email')
      .required('This field is required.'),
    phone: yup.string().required('This field is required.'),
    roleLabel: yup
      .string()
      .oneOf(ROLE_LABELS, 'Please select a role')
      .required('Please select a role'),
  })

  const {
    register,
    handleSubmit,
    formState,
    reset: resetForm,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
  })

  const toast = useToast()
  const [inviteStaffMember, { isLoading }] = useInviteStaffMemberMutation()

  const onSubmit: SubmitHandler<FormData> = async ({
    firstName,
    lastName,
    email,
    phone,
    roleLabel,
  }) => {
    const role = ROLE_OPTIONS.find(o => o.label === roleLabel)?.role
    if (!role) return

    try {
      await inviteStaffMember({
        firstName,
        lastName,
        email,
        phone,
        role,
      }).unwrap()

      toast({
        position: 'top-right',
        render: () => (
          <Toast type="success">
            Invitation sent — they'll receive an email to set a password.
          </Toast>
        ),
      })
      onClose()
    } catch (error) {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">
            Failed to send invitation. Please try again.
          </Toast>
        ),
      })
    }
  }

  useEffect(() => {
    if (!isOpen) {
      resetForm()
    }
  }, [isOpen, resetForm])

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={false}
      title="Invite Team Member"
      whiteBG
    >
      <div className="p-5 pt-0 space-y-5">
        <FormInput
          inputMode="text"
          registeredForm={register('firstName')}
          error={formState.errors?.firstName}
          label="First Name"
          placeholder="Enter first name"
        />
        <FormInput
          inputMode="text"
          registeredForm={register('lastName')}
          error={formState.errors?.lastName}
          label="Last Name"
          placeholder="Enter last name"
        />
        <FormInput
          inputMode="email"
          registeredForm={register('email')}
          error={formState.errors?.email}
          label="Email"
          placeholder="Enter email"
        />
        <FormInput
          inputMode="tel"
          registeredForm={register('phone')}
          error={formState.errors?.phone}
          label="Phone"
          placeholder="Enter phone number"
        />
        <FormSelect
          registeredForm={register('roleLabel')}
          error={formState.errors?.roleLabel}
          label="Role"
          placeholder="Select a role"
          options={ROLE_LABELS}
        />
        <button
          className="button w-full"
          disabled={!formState.isValid || isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {isLoading ? <Spinner size="sm" /> : 'Send Invitation'}
        </button>
      </div>
    </DefaultModal>
  )
}

export default AddTeamMemberModal
